import type React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import type { Company } from '@xing-com/crate-common-graphql-types';

import { EmptyState } from '.';
import { CompanyCard } from '../../components/company-card';
import { CompanyCardContainer } from '../../components/company-card-container';
import { MetaData } from '../../components/meta-data';
import { PageError } from '../../components/page-error';
import { ShowMoreButton } from '../../components/show-more-button';
import { CompanyCardsSkeletonLoggedin } from '../../components/skeleton';
import { MANAGE_USERS_PERMISSION } from '../../config/constants';
import { useAppcues } from '../../hooks/use-appcues/use-appcues';
import { useManagedCompanies } from '../../hooks/use-managed-companies';
import { useSearchParams } from '../../hooks/use-search-params';
import { useTeampermissionsUserResources } from '../../hooks/use-teampermissions-user-resources';
import { useTrackPage } from '../../hooks/use-track-page';
import { EXTERNAL_LINKS } from '../../routes';
import { pagesTrackingName, trackingParams } from '../../utils/tracking';
import * as Styled from './managed-companies.style';

const ManagedCompanies: React.FC = () => {
  const intl = useIntl();
  const { source: searchParamsSource } = useSearchParams();

  const { data, networkStatus, loading, pagination, error } =
    useManagedCompanies();
  const { data: teampermissionsData, loading: teampermissionsLoading } =
    useTeampermissionsUserResources({ permissions: MANAGE_USERS_PERMISSION });

  const currentCompanies = data?.viewer?.managedCompanies?.edges;

  const loadingFirstRender =
    teampermissionsLoading && loading && networkStatus !== 3;

  useTrackPage({ pageName: pagesTrackingName.managed });

  const dontHaveAnyCompany = currentCompanies && currentCompanies.length === 0;
  const comesFromOnlyfy = searchParamsSource === 'onlyfy-one';
  const shouldExecute = !error && dontHaveAnyCompany && comesFromOnlyfy;

  useAppcues(!!shouldExecute);

  if (loadingFirstRender) return <CompanyCardsSkeletonLoggedin />;
  if (error) return <PageError />;

  if (currentCompanies?.length === 0) return <EmptyState />;

  const { hasNextPage, endCursor } =
    data?.viewer?.managedCompanies?.pageInfo ?? {};

  const isPaginationDisabled = () => {
    if (loadingFirstRender) return true;

    return false;
  };

  const managedCompanies =
    teampermissionsData?.viewer?.teamPermissions?.userResources?.collection?.map(
      (item) => item?.resourceId
    ) ?? [];

  return (
    <>
      <MetaData
        index={false}
        title={intl.formatMessage({ id: 'CP_MANAGED_COMPANIES_HEADER' })}
        description={intl.formatMessage({ id: 'CP_MANAGED_COMPANIES_HEADER' })}
      />
      <Styled.Headline size="medium" as="h1">
        <FormattedMessage id="CP_MANAGED_COMPANIES_HEADER" />
      </Styled.Headline>
      <CompanyCardContainer>
        {currentCompanies?.map((item: $TSFixMe) => {
          const company = item?.node?.company as Company;
          const entityPageId =
            company?.entityPageId === null ? undefined : company?.entityPageId;
          return (
            <CompanyCard
              key={company?.id}
              company={company}
              buttons={[
                managedCompanies.includes(entityPageId) ? (
                  <Styled.TeampermissionsButton
                    key={`teampermissions-${company?.id}`}
                    size="small"
                    href={EXTERNAL_LINKS.teampermissions({
                      companyId: company?.id,
                    })}
                    trackingAttributes={
                      trackingParams.manageTeamButtonCompanyCard
                    }
                  >
                    <FormattedMessage id="CP_TEAMPERMISSIONS" />
                  </Styled.TeampermissionsButton>
                ) : null,
                <Styled.BrandmanagerButton
                  key={`brandmanager-${company?.id}`}
                  size="small"
                  variant="secondary"
                  href={EXTERNAL_LINKS.brandmanager({
                    slug: company?.entityPage?.slug,
                  })}
                  trackingAttributes={
                    trackingParams.brandmanagerButtonCompanyCard
                  }
                >
                  <FormattedMessage id="CP_BRANDMANAGER" />
                </Styled.BrandmanagerButton>,
              ]}
            />
          );
        })}
      </CompanyCardContainer>
      {hasNextPage && (
        <ShowMoreButton
          disabled={isPaginationDisabled()}
          loading={loading}
          onClick={() => pagination(endCursor as string)}
          intlKey="CP_OTHER_COMPANIES_MANAGE"
        />
      )}
    </>
  );
};

export { ManagedCompanies };
